// built-from: /value-stream-mapping-workspace v1 impl:value-stream-mapping-workspace role:implementation
// built-from: /specialist-activity-handoff v1 impl:specialist-activity-handoff role:implementation
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Boxes,
  GitPullRequestDraft,
  ListOrdered,
  Map,
  MessageSquareWarning,
  PanelLeftClose,
  PanelLeftOpen,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { readScope } from '../utils/scope';
import { useEngagement } from '../hooks/useEngagement';
import { useSession } from '../hooks/useSession';
import { useSharedStream } from '../hooks/useSharedStream';
import type { NewModel } from '../utils/flowModel';
import StreamMappingChatPanel from './StreamMappingChatPanel';
import StreamMappingStoryRail from './StreamMappingStoryRail';
import StreamBusinessSequencePanel from './StreamBusinessSequencePanel';
import StreamCurrentStateVsmPanel from './StreamCurrentStateVsmPanel';
import StreamPainFactsPanel from './StreamPainFactsPanel';
import FlowCanvas from './FlowCanvas';
import type {
  StreamWorkspaceStream,
  StreamWorkspaceTab,
} from './streamMappingWorkspaceTypes';
import type { UseSessionReturn } from '../hooks/sessionTypes';
import { EMPTY_STREAM_READ_SESSION, type StreamWorkspaceReadPanelProps } from './streamMappingWorkspaceTypes';
import '../styles/StreamMappingWorkspace.css';

const TABS: Array<{
  id: StreamWorkspaceTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'story', label: 'Story', icon: GitPullRequestDraft },
  { id: 'sequence', label: 'Sequence', icon: ListOrdered },
  { id: 'process', label: 'Process map', icon: Map },
  { id: 'vsm', label: 'Current state VSM', icon: Boxes },
  { id: 'pain', label: 'Pain points', icon: MessageSquareWarning },
];

function isWorkspaceTab(value: string | null): value is StreamWorkspaceTab {
  return value === 'story' || value === 'sequence' || value === 'process' || value === 'vsm' || value === 'pain';
}

function workspaceTabFromPart(value: string | null): StreamWorkspaceTab | null {
  switch (value) {
    case 'story':
      return 'story';
    case 'sequence':
      return 'sequence';
    case 'current-state-vsm':
    case 'baseline-freeze':
      return 'vsm';
    default:
      return null;
  }
}

function workspaceTabFromSearch(searchParams: URLSearchParams): StreamWorkspaceTab {
  const view = searchParams.get('streamView') || searchParams.get('view');
  if (isWorkspaceTab(view)) return view;
  return workspaceTabFromPart(searchParams.get('part')) ?? 'story';
}

interface StreamMappingWorkspaceProps {
  streamOverride?: StreamWorkspaceStream;
  sessionOverride?: UseSessionReturn;
  readOnly?: boolean;
  sessionSnapshot?: StreamWorkspaceReadPanelProps['session'];
}

const StreamMappingWorkspace: React.FC<StreamMappingWorkspaceProps> = props => props.readOnly
  ? <StreamMappingWorkspaceReadOnly {...props} /> : props.sessionOverride
  ? <StreamMappingWorkspaceContent {...props} sessionOverride={props.sessionOverride} />
  : <StreamMappingWorkspaceSession {...props} />;

/** The same stream asset and method views, without mounting a facilitation session. */
const StreamMappingWorkspaceReadOnly: React.FC<StreamMappingWorkspaceProps> = ({ streamOverride, sessionSnapshot }) => {
  const { streamId } = useParams<{ streamId: string }>();
  const [params, setParams] = useSearchParams();
  const stream = streamOverride || (readScope().areas as StreamWorkspaceStream[]).find(candidate => candidate.id === streamId);
  const session = sessionSnapshot || EMPTY_STREAM_READ_SESSION;
  const tab = workspaceTabFromSearch(params);
  if (!stream) return <main className="smw-page"><p className="smw-muted">We could not find that stream in the current engagement.</p></main>;
  return <main className="smw-page smw-page--read-only">
    <div className="smw-topbar"><header className="smw-header"><div><span className="smw-kicker">Stream workspace · Read only</span><h1>{stream.name}</h1>{stream.oneLiner ? <p>{stream.oneLiner}</p> : null}</div></header>
      <div className="smw-room"><span>SMEs in room</span><strong>{stream.smes?.map(person => person.name).filter(Boolean).join(', ') || 'Invite list open'}</strong></div>
    </div>
    <section className="smw-workspace" aria-label="Mapping workspace">
      <div className="smw-tabs" role="tablist" aria-label="Stream mapping views">{TABS.map(({ id, label, icon: Icon }) => <button key={id} type="button" role="tab" aria-selected={tab === id} className={`smw-tab${tab === id ? ' smw-tab--active' : ''}`} onClick={() => setParams(previous => { const next = new URLSearchParams(previous); next.set(previous.has('process') ? 'streamView' : 'view', id); return next; }, { replace: true })}><Icon size={15} aria-hidden="true" />{label}</button>)}</div>
      <div className="smw-tab-panel" role="tabpanel">
        {tab === 'story' ? <StreamMappingStoryRail stream={stream} session={session} full /> : null}
        {tab === 'sequence' ? <StreamBusinessSequencePanel stream={stream} session={session} readOnly /> : null}
        {tab === 'vsm' ? <StreamCurrentStateVsmPanel stream={stream} session={session} readOnly /> : null}
        {tab === 'pain' ? <StreamPainFactsPanel stream={stream} session={session} readOnly /> : null}
        {tab === 'process' ? <section className="smw-leaf smw-process-map-panel"><h2>Process map</h2><div className="smw-process-map-frame"><FlowCanvas newModel={session.newModel} /></div></section> : null}
      </div>
    </section>
  </main>;
};

const StreamMappingWorkspaceSession: React.FC<StreamMappingWorkspaceProps> = props => {
  const { streamId } = useParams<{ streamId: string }>();
  const stream = props.streamOverride || (readScope().areas as StreamWorkspaceStream[]).find(s => s.id === streamId);
  const session = useSession(stream?.mappingSessionId, 'discovery', stream?.sharedProcesses ? { contextId: 'shared-process-vsm' } : undefined);
  return <StreamMappingWorkspaceContent {...props} sessionOverride={session} />;
};

const StreamMappingWorkspaceContent: React.FC<StreamMappingWorkspaceProps & { sessionOverride: UseSessionReturn }> = ({
  streamOverride,
  sessionOverride,
}) => {
  const { streamId } = useParams<{ streamId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, currentEngagementId } = useEngagement();
  const [activeTab, setActiveTab] = useState<StreamWorkspaceTab>(() => {
    return workspaceTabFromSearch(searchParams);
  });
  const [storyRailCollapsed, setStoryRailCollapsed] = useState(false);

  useEffect(() => {
    setActiveTab(workspaceTabFromSearch(searchParams));
  }, [searchParams]);

  const streams = readScope().areas as StreamWorkspaceStream[];
  const stream = useMemo(
    () => streamOverride ?? streams.find((candidate) => candidate.id === streamId) ?? null,
    [streamOverride, streams, streamId],
  );

  const session = sessionOverride;
  const shared = useSharedStream(currentEngagementId, stream?.sharedProcesses ? stream.id : null);

  const selectTab = (tab: StreamWorkspaceTab) => {
    setActiveTab(tab);
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.set(previous.has('process') ? 'streamView' : 'view', tab); return next; }, { replace: true });
  };

  const factsToCheck = useMemo(() => {
    const spineChecks = session.spine.filter(
      (step) => step.state === 'uncertain' || step.needsCheck,
    ).length;
    const questionChecks = session.newModel.openQuestions.trim()
      ? session.newModel.openQuestions
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean).length
      : 0;
    return spineChecks + questionChecks;
  }, [session.newModel.openQuestions, session.spine]);

  const hasStory = session.spine.length > 0 || Boolean(session.newModel.flowText.trim());
  const hasFlow = Boolean(session.newModel.flowText.trim());
  const hasVsmDraft = Boolean(session.newModel.vsm?.processes?.length);
  const hasBaseline = Boolean(stream?.vsmBaseline);
  const smeNames = stream?.smes?.map((sme) => sme.name).filter(Boolean) ?? [];
  const progressItems = [
    { label: 'Story captured', state: hasStory ? 'done' : 'active' },
    { label: 'Sequence review', state: activeTab === 'sequence' ? 'active' : hasFlow ? 'done' : 'waiting' },
    { label: 'VSM facts', state: activeTab === 'vsm' ? 'active' : hasVsmDraft ? 'done' : 'waiting' },
    { label: 'Freeze baseline', state: hasBaseline ? 'done' : activeTab === 'vsm' ? 'ready' : 'waiting' },
  ];

  if (loading) {
    return (
      <main className="smw-page">
        <p className="smw-muted">Opening stream workspace...</p>
      </main>
    );
  }

  if (!currentEngagementId) {
    return (
      <main className="smw-page">
        <Link to="/streams" className="smw-back">
          <ArrowLeft size={16} aria-hidden="true" />
          Back to streams
        </Link>
        <div className="smw-empty" role="alert">
          Choose an engagement before opening a stream workspace.
        </div>
      </main>
    );
  }

  if (!stream) {
    return (
      <main className="smw-page">
        <Link to="/streams" className="smw-back">
          <ArrowLeft size={16} aria-hidden="true" />
          Back to streams
        </Link>
        <div className="smw-empty" role="alert">
          We could not find that stream in the current engagement.
        </div>
      </main>
    );
  }

  if (!stream.mappingSessionId) {
    return (
      <main className="smw-page">
        <Link to="/streams" className="smw-back">
          <ArrowLeft size={16} aria-hidden="true" />
          Back to streams
        </Link>
        <header className="smw-header">
          <div>
            <span className="smw-kicker">Stream workspace</span>
            <h1>{stream.name}</h1>
            {stream.oneLiner ? <p>{stream.oneLiner}</p> : null}
          </div>
        </header>
        <div className="smw-empty" role="alert">
          Start mapping this stream from the streams list to create its capture conversation.
        </div>
      </main>
    );
  }

  return (
    <main className="smw-page">
      <div className="smw-topbar">
        <Link to="/streams" className="smw-back" aria-label="Back to streams">
          <ArrowLeft size={16} aria-hidden="true" />
          Back to streams
        </Link>

        <header className="smw-header">
          <div>
            <span className="smw-kicker">Stream workspace</span>
            <h1>{stream.name}</h1>
            {stream.oneLiner ? <p>{stream.oneLiner}</p> : null}
          </div>
          <div className="smw-header-actions">
            <span className={`smw-review-pill${factsToCheck > 0 ? ' smw-review-pill--warn' : ''}`}>
              {factsToCheck > 0 ? `${factsToCheck} facts to check` : 'facts ready'}
            </span>
            <button
              type="button"
              className="smw-ghost-action"
              aria-pressed={storyRailCollapsed}
              aria-label={storyRailCollapsed ? 'Show story spine rail' : 'Hide story spine rail'}
              onClick={() => setStoryRailCollapsed((collapsed) => !collapsed)}
            >
              {storyRailCollapsed ? (
                <PanelLeftOpen size={15} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={15} aria-hidden="true" />
              )}
              {storyRailCollapsed ? 'Show story' : 'Hide story'}
            </button>
            <button type="button" className="smw-ghost-action" disabled>
              <Undo2 size={15} aria-hidden="true" />
              Undo
            </button>
            <button type="button" className="smw-primary-action" onClick={() => selectTab('vsm')}>
              <Boxes size={15} aria-hidden="true" />
              Open VSM draft
            </button>
          </div>
        </header>

        <section className="smw-progress-card" aria-label="Stream mapping progress">
          <div className="smw-progress-track">
            {progressItems.map((item) => (
              <span
                key={item.label}
                className={`smw-progress-step smw-progress-step--${item.state}`}
              >
                {item.label}
              </span>
            ))}
          </div>
          <div className="smw-room">
            <span>SMEs in room</span>
            <strong>{smeNames.length ? smeNames.join(', ') : 'Invite list open'}</strong>
          </div>
        </section>
      </div>

      <div className={`smw-layout${storyRailCollapsed || stream.sharedProcesses ? ' smw-layout--story-collapsed' : ''}`}>
        {!storyRailCollapsed && !stream.sharedProcesses ? (
          <StreamMappingStoryRail stream={stream} session={session} />
        ) : null}

        <section className="smw-workspace" aria-label="Mapping workspace">
          <div className="smw-workspace-head">
            <div>
              <span>Mapping workspace</span>
              <strong>{TABS.find((tab) => tab.id === activeTab)?.label}</strong>
            </div>
            <span>{session.phase === 'narrate' ? 'Narrating' : 'Refining'}</span>
          </div>

          <div className="smw-tabs" role="tablist" aria-label="Stream mapping views">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`smw-tab${activeTab === tab.id ? ' smw-tab--active' : ''}`}
                  onClick={() => selectTab(tab.id)}
                >
                  <Icon size={15} aria-hidden={true} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="smw-tab-panel" role="tabpanel">
            {activeTab === 'story' && !stream.sharedProcesses ? (
              <StreamMappingStoryRail stream={stream} session={session} full />
            ) : null}
            {activeTab === 'story' && stream.sharedProcesses ? <StreamBusinessSequencePanel stream={stream} session={session} /> : null}
            {activeTab === 'sequence' ? (
              <StreamBusinessSequencePanel stream={stream} session={session} />
            ) : null}
            {activeTab === 'process' ? (
              <section className="smw-leaf smw-process-map-panel" data-testid="stream-process-map-panel">
                <div className="smw-panel-head">
                  <div>
                    <h2>Process map</h2>
                    <p>The DSN-backed process flow captured in chat.</p>
                  </div>
                  <div className="smw-panel-head-actions">
                    <button
                      type="button"
                      className="smw-panel-action"
                      onClick={() => selectTab('story')}
                    >
                      <ArrowLeft size={15} aria-hidden="true" />
                      Back to story
                    </button>
                    <Map size={18} aria-hidden="true" />
                  </div>
                </div>
                {stream.sharedProcesses ? shared.shared?.processes?.map(process => <section key={process.processId}><h3>{process.name}</h3><div className="smw-process-map-frame"><FlowCanvas newModel={{ ...session.newModel, flowText: process.domainFlowText || '' } as NewModel} /></div></section>) : session.newModel.flowText.trim() ? (
                  <div className="smw-process-map-frame">
                    <FlowCanvas newModel={session.newModel} />
                  </div>
                ) : (
                  <p className="smw-muted">Keep narrating and the process map will appear here.</p>
                )}
              </section>
            ) : null}
            {activeTab === 'vsm' ? (
              <StreamCurrentStateVsmPanel stream={stream} session={session} />
            ) : null}
            {activeTab === 'pain' ? (
              <StreamPainFactsPanel stream={stream} session={session} />
            ) : null}
          </div>
        </section>

        <div className="smw-side-panel">
          <StreamMappingChatPanel stream={stream} session={session} />
          <aside className="smw-next-card" aria-label="Workshop focus">
            <span>Workshop focus</span>
            <strong>
              {activeTab === 'vsm'
                ? 'Turn captured facts into the current-state readout.'
                : 'Narrate, correct, and reorder without leaving the stream.'}
            </strong>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default StreamMappingWorkspace;
